/**
 * Get the pictures the first screen needs, and only those, before it opens.
 *
 * The screens are painted artwork: each one is a slab or a scene the stylesheet
 * reaches for by absolute path. Fetched at the moment a screen appears, they
 * arrive a beat late and the screen builds itself in front of the user.
 *
 * The fix used to be to fetch all of it at boot — fifteen megabytes, heaviest
 * first, behind the splash. That solved the flicker and bought two new
 * problems: a phone on a slow connection stared at a logo for the better part
 * of a minute, and it paid for the roulette, the daily combo and every sheet
 * whether or not the person ever opened them.
 *
 * So the manifest now says what each picture is *for* (§16), and this fetches
 * it in three waves:
 *
 *   - **critical** — the first screen and the bar that sits on every screen
 *     after it. The splash waits for this, and this is ~3.4 MB rather than 15.
 *   - **high** — the five places the bar leads to. Started the moment the app
 *     opens, behind it, so moving is instant and nothing is held up.
 *   - **lazy** — the roulette, the combo, the sheets. Asked for by the screen
 *     that wants them.
 *
 * Two things this still deliberately does not do:
 *
 *  - It never fails the boot. A picture that 404s or a manifest that is missing
 *    counts as done and the app opens; the worst case is the old behaviour,
 *    where the image arrives with the screen that needs it.
 *  - It never holds the splash forever. Past DEADLINE_MS the app opens and the
 *    rest keeps loading behind it.
 */

export type AssetTier = 'critical' | 'high' | 'lazy';

interface ManifestFile {
  url: string;
  bytes: number;
  hash: string;
  tier: AssetTier;
}

interface Manifest {
  revision: string;
  files: ManifestFile[];
  totalBytes: number;
  bytesByTier: Record<AssetTier, number>;
}

/** How many at once. Enough to fill a phone's pipe, not enough to thrash it. */
const CONCURRENCY = 6;

/**
 * Past this, the app opens anyway and the rest arrives behind it.
 *
 * Shorter than it was, because what is being waited for is now a third of what
 * it was. A critical tier that has not arrived in twelve seconds is not going
 * to arrive in twenty either, and the screen degrades to painting as the
 * pictures land rather than to nothing.
 */
const DEADLINE_MS = 12_000;

export interface PreloadProgress {
  /** Bytes in hand, of `total`. */
  loaded: number;
  total: number;
  /** 0 to 1. Stays at 0 until the manifest says how much there is. */
  ratio: number;
  /** True once everything is in hand, or the deadline passed, or nothing to do. */
  done: boolean;
}

const IDLE: PreloadProgress = { loaded: 0, total: 0, ratio: 0, done: false };

/** Read once per load; the tiers after the first reuse it. */
let manifestPromise: Promise<Manifest | null> | null = null;

function loadManifest(): Promise<Manifest | null> {
  manifestPromise ??= (async () => {
    try {
      const response = await fetch('/image-manifest.json');
      if (!response.ok) throw new Error(String(response.status));
      const manifest = (await response.json()) as Manifest;
      if (!Array.isArray(manifest.files) || manifest.files.length === 0) throw new Error('empty');
      return manifest;
    } catch {
      return null;
    }
  })();
  return manifestPromise;
}

/**
 * Pictures that must show the instant they are raised: the notice panels, the
 * toast plates and the level-up's jump, which appear over any screen with a
 * sound of their own. In
 * the cache is not enough for them — a 1024 × 1536 panel still has to be
 * decoded, and that is the beat between the sound and the picture. So these are
 * decoded as they arrive and the decoded image is held for the life of the page.
 */
const KEEP_DECODED = ['/ui/notice/', '/ui/toast/', '/ui/levelup/'];
const held: HTMLImageElement[] = [];

/** One picture into the browser's cache. Resolves either way. */
function fetchImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const image = new Image();
    image.decoding = 'async';
    const finish = () => resolve();
    image.onload = () => {
      if (KEEP_DECODED.some((prefix) => url.startsWith(prefix))) {
        held.push(image);
        void image.decode?.().catch(() => {});
      }
      finish();
    };
    image.onerror = finish;
    image.src = url;
  });
}

async function fetchAll(
  files: ManifestFile[],
  onProgress?: (loadedBytes: number) => void,
): Promise<void> {
  const queue = [...files];
  let loaded = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const file = queue.shift();
      if (!file) return;
      await fetchImage(file.url);
      loaded += file.bytes;
      onProgress?.(loaded);
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
}

/**
 * The critical tier. Resolves when the app should open — which is when that
 * tier is in hand, or when the deadline says to stop waiting.
 */
export async function preloadImages(onProgress: (progress: PreloadProgress) => void): Promise<void> {
  const manifest = await loadManifest();
  if (!manifest) {
    onProgress({ ...IDLE, done: true });
    return;
  }

  const critical = manifest.files.filter((file) => file.tier === 'critical');
  const total = critical.reduce((sum, file) => sum + file.bytes, 0);
  if (total === 0) {
    onProgress({ ...IDLE, done: true });
    return;
  }

  let released = false;
  const report = (loaded: number, done: boolean) => {
    onProgress({ loaded, total, ratio: Math.min(1, loaded / total), done });
  };
  report(0, false);

  const everything = fetchAll(critical, (loaded) => report(loaded, false)).then(() => {
    released = true;
    report(total, true);
  });

  // Whichever comes first. The workers carry on either way: the pictures they
  // finish after the app opens are still in the cache for the screen that
  // wants them.
  await Promise.race([
    everything,
    new Promise<void>((resolve) =>
      setTimeout(() => {
        if (!released) report(0, true);
        resolve();
      }, DEADLINE_MS),
    ),
  ]);
}

/** Tiers already asked for, so a second request is not a second download. */
const warmed = new Set<AssetTier>();

/**
 * How long to wait for a freshly registered worker to take control.
 *
 * Registration, install and `clients.claim()` take a moment, and for that
 * moment `controller` is null even though a worker is about to exist. Warming
 * in the page during that window is not wrong, but it is wasted: those bytes
 * land in the HTTP cache and not in the worker's, so the next session pays for
 * them again. Waiting a second to hand the job to the worker instead is the
 * difference between warming once and warming every time.
 */
const CONTROLLER_WAIT_MS = 1_500;

/** The controlling worker, waiting briefly for one that is on its way. */
function controller(): Promise<ServiceWorker | null> {
  const container = navigator.serviceWorker as ServiceWorkerContainer | undefined;
  if (!container) return Promise.resolve(null);
  if (container.controller) return Promise.resolve(container.controller);

  return new Promise((resolve) => {
    let settled = false;
    const done = (worker: ServiceWorker | null) => {
      if (settled) return;
      settled = true;
      container.removeEventListener('controllerchange', onChange);
      resolve(worker);
    };
    const onChange = () => done(container.controller);
    container.addEventListener('controllerchange', onChange);
    setTimeout(() => done(container.controller), CONTROLLER_WAIT_MS);
  });
}

/**
 * Fetches a tier in the background.
 *
 * Handed to the service worker when there is one — it can cache across
 * sessions, which an `Image` in a page cannot — and done in the page when
 * there is not, so a browser without service workers still gets the benefit
 * within the session.
 */
export async function warmTier(tier: AssetTier): Promise<void> {
  if (warmed.has(tier)) return;
  warmed.add(tier);

  const worker = await controller();
  if (worker) {
    worker.postMessage({ type: 'warm-tier', tier });
    return;
  }

  const manifest = await loadManifest();
  if (!manifest) return;
  await fetchAll(manifest.files.filter((file) => file.tier === tier));
}

/**
 * Registers the artwork cache (§15).
 *
 * Deliberately after the app has opened rather than before: installing fetches
 * the critical tier, and doing that in parallel with the page fetching the
 * same files would double the bytes on the one connection that matters most.
 *
 * Failure here is silent on purpose. No service worker means the browser's own
 * cache and the headers the server sets, which is what the app had before —
 * worse, but working.
 */
export function registerArtworkCache(): void {
  if (!('serviceWorker' in navigator)) return;
  // A dev server rebuilds these on every change; a worker caching them would
  // serve yesterday's artwork all morning.
  if (import.meta.env.DEV) return;

  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
    // Blocked, unsupported, or served from a context that forbids it.
  });
}
